import {
  Archive,
  BriefcaseBusiness,
  ChevronRight,
  FolderKanban,
  MessagesSquare,
  MoreHorizontal,
  Pencil,
  Settings,
  SquarePen,
  Trash2,
  X
} from "lucide-react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { motion, useReducedMotion } from "framer-motion";
import { useMemo, useState } from "react";

import { OrgSwitcher } from "@/components/OrgSwitcher";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useChatHistory } from "@/contexts/ChatHistoryContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useProjects } from "@/hooks/queries/useProjects";
import { usePrefetchOnIntent } from "@/hooks/usePrefetchOnIntent";
import { queryClient } from "@/lib/query";
import { type ChatRow } from "@/lib/api";
import { chatInfiniteRouteQuery, prefetchProjectRouteData } from "@/lib/routeQueries";
import { instantTransition, standardTransition } from "@/lib/motion";
import { cn } from "@/lib/utils";

// How many recent matters / chats the rail peeks show before "See all".
const RAIL_PEEK_LIMIT = 5;

const navItems = [
  { href: "/assistant", label: "New chat", icon: SquarePen },
  { href: "/chats", label: "All chats", icon: MessagesSquare },
  { href: "/projects", label: "Matters", icon: FolderKanban },
] as const;

function initials(value: string | null | undefined) {
  const source = value?.trim() || "CM";
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function SectionToggle({ open, label, onClick }: { open: boolean; label: string; onClick: () => void }) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold uppercase text-muted-foreground hover:text-foreground">
      <motion.span className="flex size-3.5 items-center justify-center" animate={{ rotate: open ? 90 : 0 }} transition={prefersReducedMotion ? instantTransition : standardTransition} aria-hidden>
        <ChevronRight className="size-3.5" />
      </motion.span>
      <span>{label}</span>
    </button>
  );
}

function ProjectNavLink({ project, enabled }: { project: { id: string; name: string }; enabled: boolean }) {
  const prefetchHandlers = usePrefetchOnIntent(() => prefetchProjectRouteData(queryClient, project.id), enabled);

  return (
    <Link
      to="/projects/$id"
      params={{ id: project.id }}
      search={{ tab: "assistant" }}
      {...prefetchHandlers}
      className="flex min-h-9 items-center gap-2 rounded-md px-3 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
    >
      <BriefcaseBusiness className="size-4 shrink-0" aria-hidden />
      <span className="truncate">{project.name}</span>
    </Link>
  );
}

/** Route-aware chat link: a matter chat opens in its matter context, a scratchpad chat globally. */
function ChatNavLink({
  chat,
  currentChatId,
  enabled,
  canManage,
  projects,
  onSelect,
  onRename,
  onMove,
  onArchive,
  onDelete,
}: {
  chat: ChatRow;
  currentChatId: string | null;
  enabled: boolean;
  canManage: boolean;
  projects: Array<{ id: string; name: string }>;
  onSelect: () => void;
  onRename: () => void;
  onMove: (projectId: string | null) => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const prefetchHandlers = usePrefetchOnIntent(() => queryClient.prefetchInfiniteQuery(chatInfiniteRouteQuery(chat.id)), enabled);
  const linkClassName = cn(
    "min-w-0 flex-1 px-3 py-2 text-sm text-muted-foreground hover:text-accent-foreground",
    currentChatId === chat.id && "bg-secondary text-secondary-foreground"
  );
  const rowClassName = cn(
    "group flex min-h-10 items-stretch overflow-hidden rounded-md hover:bg-accent hover:text-accent-foreground",
    currentChatId === chat.id && "bg-secondary text-secondary-foreground"
  );
  const label = <span className="line-clamp-2">{chat.title || "Untitled chat"}</span>;
  const link = chat.project_id ? (
    <Link to="/projects/$id/assistant/chat/$chatId" params={{ id: chat.project_id, chatId: chat.id }} onClick={onSelect} {...prefetchHandlers} className={linkClassName}>
      {label}
    </Link>
  ) : (
    <Link to="/assistant/chat/$id" params={{ id: chat.id }} onClick={onSelect} {...prefetchHandlers} className={linkClassName}>
      {label}
    </Link>
  );

  return (
    <div className={rowClassName}>
      {link}
      {canManage ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-10 shrink-0 items-center justify-center text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring group-hover:opacity-100 data-[state=open]:opacity-100"
              aria-label={`Manage ${chat.title || "untitled chat"}`}
            >
              <MoreHorizontal className="size-4" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={onRename}>
              <Pencil className="size-4" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FolderKanban className="size-4" />
                Move to matter
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-72 w-64 overflow-y-auto">
                <DropdownMenuItem onClick={() => onMove(null)}>
                  No matter
                  {!chat.project_id ? <span className="ml-auto text-xs text-muted-foreground">Current</span> : null}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {projects.length ? (
                  projects.map((project) => (
                    <DropdownMenuItem key={project.id} onClick={() => onMove(project.id)}>
                      <span className="truncate">{project.name}</span>
                      {chat.project_id === project.id ? <span className="ml-auto text-xs text-muted-foreground">Current</span> : null}
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem disabled>No matters</DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={onArchive}>
              <Archive className="size-4" />
              Archive
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-destructive">
              <Trash2 className="size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

export function AppSidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, credits } = useUserProfile();
  const { chats, currentChatId, setCurrentChatId, renameChat, moveChat, archiveChat, deleteChat } = useChatHistory();
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [renamingChat, setRenamingChat] = useState<ChatRow | null>(null);
  const [pendingDeleteChat, setPendingDeleteChat] = useState<ChatRow | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [renamingError, setRenamingError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { data: projectRows } = useProjects(undefined, { enabled: Boolean(user) });

  const recentProjects = useMemo(
    () =>
      [...(projectRows ?? [])]
        .sort((a, b) => {
          const aTime = Date.parse(a.updated_at ?? a.created_at ?? "");
          const bTime = Date.parse(b.updated_at ?? b.created_at ?? "");
          return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
        })
        .slice(0, RAIL_PEEK_LIMIT),
    [projectRows]
  );
  const recentChats = chats.slice(0, RAIL_PEEK_LIMIT);

  const userLabel = profile?.displayName || user?.email || "Account";
  const activeNav = useMemo(() => navItems.find((item) => pathname === item.href || pathname.startsWith(`${item.href}/`)), [pathname]);
  const manageableProjects = useMemo(() => (projectRows ?? []).map((project) => ({ id: project.id, name: project.name })), [projectRows]);

  const runChatAction = async (action: () => Promise<void>, failMessage: string) => {
    setActionError(null);
    try {
      await action();
      setActionError(null);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : failMessage);
    }
  };

  const openRename = (chat: ChatRow) => {
    setActionError(null);
    setRenamingError(null);
    setRenamingChat(chat);
    setRenameTitle(chat.title ?? "");
  };

  const saveRename = async () => {
    if (!renamingChat) return;
    const title = renameTitle.trim();
    if (!title) {
      setRenamingError("Chat title is required.");
      return;
    }
    setRenamingError(null);
    try {
      await renameChat(renamingChat.id, title);
      setRenamingChat(null);
    } catch (caught) {
      setRenamingError(caught instanceof Error ? caught.message : "Could not rename the chat.");
    }
  };

  const moveAndNavigate = async (chat: ChatRow, projectId: string | null) => {
    await moveChat(chat.id, projectId);
    if (currentChatId !== chat.id) return;
    try {
      if (projectId) {
        await navigate({ to: "/projects/$id/assistant/chat/$chatId", params: { id: projectId, chatId: chat.id } });
      } else {
        await navigate({ to: "/assistant/chat/$id", params: { id: chat.id } });
      }
    } catch (caught) {
      console.warn("Chat moved, but navigation to the new chat route failed.", caught);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDeleteChat) return;
    const chatId = pendingDeleteChat.id;
    await runChatAction(async () => {
      await deleteChat(chatId);
      setPendingDeleteChat(null);
    }, "Could not delete the chat.");
  };

  const sidebar = (
    <aside
      id="app-sidebar"
      className={cn(
        "fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r bg-card shadow-md transition-transform md:sticky md:shadow-none",
        sidebarOpen ? "translate-x-0" : "-translate-x-full md:w-16 md:translate-x-0"
      )}
    >
      <div className={cn("flex min-h-12 items-center border-b px-2", sidebarOpen ? "justify-start" : "justify-center")}>
        <OrgSwitcher collapsed={!sidebarOpen} />
      </div>

      <nav className="space-y-1 px-2 pt-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = activeNav?.href === item.href;

          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
                active && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                !sidebarOpen && "justify-center px-0"
              )}
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              {sidebarOpen ? <span>{item.label}</span> : null}
            </Link>
          );
        })}
      </nav>

      {sidebarOpen ? (
        <>
          <Separator className="my-3" />
          <div className="min-h-0 flex-1 overflow-y-auto px-2">
            <SectionToggle open={projectsOpen} label="Recent matters" onClick={() => setProjectsOpen((open) => !open)} />
            {projectsOpen ? (
              <div className="mb-3 space-y-1">
                {recentProjects.length ? (
                  recentProjects.map((project) => (
                    <ProjectNavLink key={project.id} project={project} enabled={Boolean(user)} />
                  ))
                ) : (
                  <p className="px-3 py-2 text-sm text-muted-foreground">No matters yet.</p>
                )}
              </div>
            ) : null}

            <SectionToggle open={historyOpen} label="Recent chats" onClick={() => setHistoryOpen((open) => !open)} />
            {historyOpen ? (
              <div className="space-y-1">
                {actionError ? (
                  <p className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    <span className="min-w-0 flex-1">{actionError}</span>
                    <button type="button" className="flex size-6 shrink-0 items-center justify-center rounded-sm hover:bg-destructive/10" aria-label="Dismiss error" onClick={() => setActionError(null)}>
                      <X className="size-3" aria-hidden />
                    </button>
                  </p>
                ) : null}
                {recentChats.length ? (
                  recentChats.map((chat) => (
                    <ChatNavLink
                      key={chat.id}
                      chat={chat}
                      currentChatId={currentChatId}
                      enabled={Boolean(user)}
                      canManage={Boolean(user?.uid) && chat.user_id === user?.uid}
                      projects={manageableProjects}
                      onSelect={() => setCurrentChatId(chat.id)}
                      onRename={() => openRename(chat)}
                      onMove={(projectId) => void runChatAction(() => moveAndNavigate(chat, projectId), "Could not move the chat.")}
                      onArchive={() => void runChatAction(() => archiveChat(chat.id), "Could not archive the chat.")}
                      onDelete={() => setPendingDeleteChat(chat)}
                    />
                  ))
                ) : (
                  <p className="px-3 py-2 text-sm text-muted-foreground">No saved chats.</p>
                )}
                <Link to="/chats" className="block rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                  See all chats
                </Link>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {sidebarOpen ? (
        <div className="border-t p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-accent">
                <Avatar>
                  <AvatarFallback>{initials(userLabel)}</AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{userLabel}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {credits === null ? "Credits loading" : <span className="tabular-nums">{credits.toLocaleString()} credits</span>}
                  </span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => void navigate({ to: "/account" })}>
                <Settings className="size-4" />
                Account
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </aside>
  );

  return (
    <>
      {sidebarOpen ? <button type="button" aria-label="Close navigation overlay" className="fixed inset-0 z-30 bg-foreground/20 md:hidden" onClick={() => setSidebarOpen(false)} /> : null}
      {sidebar}
      <Dialog open={Boolean(renamingChat)} onOpenChange={(open) => { if (!open) setRenamingChat(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename chat</DialogTitle>
          </DialogHeader>
          <Input
            aria-label="New chat title"
            value={renameTitle}
            autoFocus
            onChange={(event) => setRenameTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                void saveRename();
              }
            }}
          />
          {renamingError ? <p className="text-sm text-destructive">{renamingError}</p> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setRenamingChat(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void saveRename()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(pendingDeleteChat)} onOpenChange={(open) => { if (!open) setPendingDeleteChat(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete chat</DialogTitle>
            <DialogDescription>
              Delete &quot;{pendingDeleteChat?.title || "Untitled chat"}&quot;? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setPendingDeleteChat(null)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void confirmDelete()}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
